import React, { useMemo, useState } from "react"
import { Plus } from "lucide-react"

const StatusBadge = ({ label, statusClass }) => (
  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${statusClass}`}>
    {label}
  </span>
)

const SiteVisitsAdminList = ({
  adminStaff = [],
  loading = false,
  statusByEmail = {},
  statusesLoading = false,
  currentYear,
  getStatusLabel,
  getStatusClass,
  onInitiateSiteVisit,
  onCaptureObservations,
  onCompleteEvaluation,
  onViewSiteVisit,
}) => {
  const [searchQuery, setSearchQuery] = useState("")

  const filteredStaff = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return adminStaff

    return adminStaff.filter((admin) => {
      const fullName = `${admin.firstName || ""} ${admin.lastName || ""}`.toLowerCase()
      const email = (admin.email || "").toLowerCase()
      return fullName.includes(query) || email.includes(query)
    })
  }, [adminStaff, searchQuery])

  const getAdminStatus = (admin) => {
    const data = statusByEmail[admin.email?.toLowerCase()] || { siteVisits: [] }
    const siteVisit = data.siteVisits.find((item) => item.year === currentYear)
    const hasActive =
      siteVisit?.status === "initiated" ||
      siteVisit?.status === "observations_captured" ||
      siteVisit?.status === "submitted_by_admin"

    return { siteVisit, hasActive }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="font-semibold text-[#241763]">Admin Staff</h3>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full sm:w-72 p-2 border rounded text-sm"
          aria-label="Search admin staff"
        />
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-gray-100 font-bold">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Observation / Site Visit</th>
            </tr>
          </thead>
          <tbody>
            {loading || statusesLoading ? (
              <tr>
                <td colSpan={3} className="p-4 text-center text-gray-500">
                  Loading admin staff and statuses...
                </td>
              </tr>
            ) : filteredStaff.length === 0 ? (
              <tr>
                <td colSpan={3} className="p-4 text-center text-gray-500">
                  No admin staff found.
                </td>
              </tr>
            ) : (
              filteredStaff.map((admin) => {
                const status = getAdminStatus(admin)

                return (
                  <tr key={admin.id} className="border-t align-top hover:bg-gray-50">
                    <td className="p-3 font-medium whitespace-nowrap">
                      {admin.firstName} {admin.lastName}
                    </td>
                    <td className="p-3 text-gray-600">{admin.email}</td>
                    <td className="p-3">
                      <div className="flex flex-col gap-2 items-start">
                        <StatusBadge
                          label={
                            status.siteVisit
                              ? getStatusLabel(status.siteVisit.status)
                              : "Not Started"
                          }
                          statusClass={
                            status.siteVisit
                              ? getStatusClass(status.siteVisit.status)
                              : "bg-gray-100 text-gray-600"
                          }
                        />
                        {!status.siteVisit && (
                          <button
                            type="button"
                            onClick={() => onInitiateSiteVisit(admin)}
                            className="flex items-center gap-1 px-3 py-1 bg-[#4472C4] text-white rounded text-xs hover:opacity-90"
                          >
                            <Plus className="h-3 w-3" />
                            Observation/Site Visit
                          </button>
                        )}
                        {status.siteVisit?.status === "initiated" && (
                          <button
                            type="button"
                            onClick={() =>
                              onCaptureObservations(admin, status.siteVisit)
                            }
                            className="px-3 py-1 bg-[#ED7D31] text-white rounded text-xs hover:opacity-90"
                          >
                            Capture Observation Evidence
                          </button>
                        )}
                        {status.siteVisit?.status === "observations_captured" && (
                          <span className="text-xs text-gray-500">
                            Waiting for admin to complete Post-Observation
                          </span>
                        )}
                        {status.siteVisit?.status === "submitted_by_admin" && (
                          <button
                            type="button"
                            onClick={() =>
                              onCompleteEvaluation(admin, status.siteVisit)
                            }
                            className="px-3 py-1 bg-[#241763] text-white rounded text-xs hover:bg-[#1a1047]"
                          >
                            Complete Evaluation
                          </button>
                        )}
                        {status.siteVisit?.status === "completed" && (
                          <button
                            type="button"
                            onClick={() => onViewSiteVisit(admin, status.siteVisit)}
                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                          >
                            View
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default SiteVisitsAdminList
